import React, { useEffect, useRef, useState } from "react";
import Header from "../../components/header";
import SearchPanel from "../../components/searchPanel";
import SearchList from "../../components/searchPanel/searchList";
import SectionBuilder from "./sectionBuilder";
import axios from 'axios';
import ScrollUpIconSvg from "../../assets/img/scroll_up_icon.svg";
import MobileScrollUpIconSvg from "../../assets/img/mobile_scroll_up.svg";
import "./style.css";

const HomePage = () => {
  const scrollRef = useRef(null);
  const [timeoutID, setTimeoutID] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriesList, setCategoriesList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [questionsList, setQuestionsList] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [queryFilter, setQueryFilter] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 800);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    const categoryParam = url.searchParams.get('category');
    
    if (categoryParam && categoriesList.length > 0) {
      const category = categoriesList.find(cat => {
        const categorySlug = cat.name
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s_-]+/g, '-')
          .replace(/^-+|-+$/g, '');
        return categorySlug === categoryParam;
      });
      if (category) {
        setSelectedCategory(category.id);
      }
    }
  }, [categoriesList]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 800);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleVisibility = () => {
    if (window.scrollY > 100) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_QA_API}/docs/categories/list?is_active=true`);
        const parentId = res.data.data.find((item) => item.name === 'GreenifAI')?.id;
        const categoryId = res.data.data.find((item) => item.name === 'TT')?.id;
        const filteredData = res.data.data.filter((item) => item.parent_id !== parentId && item.parent_id !== null);
        setCategoriesList(filteredData);

        // Get category from url
        const url = new URL(window.location.href);
        const categoryParam = url.searchParams.get('category');
        
        let initialCategoryId = categoryId;
        if (categoryParam) {
          const category = filteredData.find(cat => {
            const categorySlug = cat.name
              .toLowerCase()
              .trim()
              .replace(/[^\w\s-]/g, '')
              .replace(/[\s_-]+/g, '-')
              .replace(/^-+|-+$/g, '');
            return categorySlug === categoryParam;
          });
          if (category) {
            initialCategoryId = category.id;
          }
        }
        
        // Load questions
        setIsLoading(true);
        let questionsUrl = `${process.env.REACT_APP_QA_API}/help/qa?language=en`;
        if (initialCategoryId) {
          questionsUrl += `&category_id=${initialCategoryId}`;
        }
        try {
          const questionsRes = await axios.post(questionsUrl);
          setQuestionsList(questionsRes.data.data.doc);
          setSelectedCategory(initialCategoryId);
        } catch (err) {
          console.log(err.response.data.data);
        } finally {
          setIsLoading(false);
        }
      } catch (err) {
        console.log(err.response.data.data);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!selectedCategory) return; // Don't load questions if category is not selected
      
      setIsLoading(true);
      let url = `${process.env.REACT_APP_QA_API}/help/qa?language=en`;
      if (queryFilter) {
        url += `&q=${queryFilter}`;
      }
      if (selectedCategory) {
        url += `&category_id=${selectedCategory}`;
      }
      try {
        const res = await axios.post(url);
        setQuestionsList(res.data.data.doc);
        if (selectedCategory) {
          scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      } catch (err) {
        console.log(err.response.data.data);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [queryFilter, selectedCategory]);

  const handleSearchChange = (searchWord) => {
    setSearchTerm(searchWord);

    if (timeoutID) {
      clearTimeout(timeoutID);
    }
    if (searchWord.length >= 3 || !searchWord) {
      setErrorMessage('');
      const timeout = setTimeout(() => {
        setQueryFilter(searchWord);
      }, 1000);
      setTimeoutID(timeout);
    } else {
      setErrorMessage("You must write minimun 3 characters")
    }

  };

  return (
    <div className="page-container home">
      {scrolled && (
        <button className="scroll-top-btn" onClick={scrollToTop}>
          <img src={!isSmallScreen ? ScrollUpIconSvg : MobileScrollUpIconSvg} className="arrow"/>
        </button>
      )}
      {!isSmallScreen && (
        <video
          className="video"
          loop
          autoPlay
          muted
          playsInline
          onError={(e) => console.error('Video error', e)}
        >
          <source src={"./../../../../video/Islands-2119.mp4"} type="video/mp4"/>
        </video>
      )}
      {isSmallScreen && (
        <img
          className="video mobile-bg-image"
          src={require("../../assets/img/bg.png")}
          alt="background"
        />
      )}
      <Header />
      <div className="general-container">
        <div className="primary-container">
          <SearchPanel
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            errorMessage={errorMessage}
          />
          <div className="secondary-container">
            <div className="search-container">
              {categoriesList.length > 0 && (
                <SectionBuilder
                  categoriesList={categoriesList}
                  setSelectedCategory={setSelectedCategory}
                  selectedCategory={selectedCategory}
                />
              )}
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
              ) : (
                questionsList && questionsList.length > 0 && (
                  <SearchList
                    scrollRef={scrollRef}
                    searchResults={questionsList}
                    selectedCategory={selectedCategory}
                    categoriesList={categoriesList}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
